import React, { useState } from "react";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Tooltip,
  Snackbar,
  Alert,
  Skeleton,
  Collapse,
  Autocomplete,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore,
  ExpandLess,
  PersonRemove,
  PersonAdd,
} from "@mui/icons-material";
import {
  useGetOrganizationsQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useAddUserToOrgMutation,
  useRemoveUserFromOrgMutation,
  useGetUsersQuery,
} from "../../redux/slices/djangoApi";

const Organizations = () => {
  const { data: orgsData, isLoading } = useGetOrganizationsQuery();
  const { data: usersData } = useGetUsersQuery();
  const [createOrg] = useCreateOrganizationMutation();
  const [updateOrg] = useUpdateOrganizationMutation();
  const [deleteOrg] = useDeleteOrganizationMutation();
  const [addUser] = useAddUserToOrgMutation();
  const [removeUser] = useRemoveUserFromOrgMutation();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [addUserTarget, setAddUserTarget] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const organizations = orgsData?.results || orgsData || [];
  const allUsers = usersData?.results || usersData || [];

  const handleCreate = async () => {
    try {
      await createOrg(form).unwrap();
      setCreateOpen(false);
      setForm({ name: "", description: "" });
      setSnackbar({ severity: "success", message: "Organization created." });
    } catch {
      setSnackbar({
        severity: "error",
        message: "Failed to create organization.",
      });
    }
  };

  const handleEdit = async () => {
    try {
      await updateOrg({ id: editTarget.id, data: form }).unwrap();
      setEditTarget(null);
      setForm({ name: "", description: "" });
      setSnackbar({ severity: "success", message: "Organization updated." });
    } catch {
      setSnackbar({
        severity: "error",
        message: "Failed to update organization.",
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteOrg(deleteTarget.id).unwrap();
      setDeleteTarget(null);
      setSnackbar({ severity: "success", message: "Organization deleted." });
    } catch {
      setSnackbar({
        severity: "error",
        message: "Failed to delete organization.",
      });
    }
  };

  const handleAddUser = async () => {
    if (!selectedUser) return;
    try {
      await addUser({
        orgId: addUserTarget.id,
        userId: selectedUser.id,
      }).unwrap();
      setSelectedUser(null);
      setSnackbar({ severity: "success", message: "User added." });
    } catch {
      setSnackbar({ severity: "error", message: "Failed to add user." });
    }
  };

  const handleRemoveUser = async (orgId, userId) => {
    try {
      await removeUser({ orgId, userId }).unwrap();
      setSnackbar({ severity: "success", message: "User removed." });
    } catch {
      setSnackbar({ severity: "error", message: "Failed to remove user." });
    }
  };

  const openEdit = (org) => {
    setEditTarget(org);
    setForm({ name: org.name, description: org.description || "" });
  };

  const getAvailableUsers = (org) => {
    const orgUserIds = (org.users || []).map((u) => u.id);
    return allUsers.filter((u) => !orgUserIds.includes(u.id));
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold">
          Organizations
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setForm({ name: "", description: "" });
            setCreateOpen(true);
          }}
        >
          Create Organization
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Users</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : organizations.map((org) => (
                  <React.Fragment key={org.id}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setExpandedOrg(
                              expandedOrg === org.id ? null : org.id
                            )
                          }
                        >
                          {expandedOrg === org.id ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="medium">{org.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography color="text.secondary">
                          {org.description || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${(org.users || []).length} user(s)`}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton onClick={() => openEdit(org)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={() => setDeleteTarget(org)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{ py: 0, borderBottom: "none" }}
                      >
                        <Collapse in={expandedOrg === org.id}>
                          <Box sx={{ py: 2, pl: 6 }}>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={1}
                            >
                              <Typography variant="subtitle2" fontWeight="bold">
                                Members
                              </Typography>
                              <Tooltip title="Add user">
                                <IconButton
                                  size="small"
                                  onClick={() => setAddUserTarget(org)}
                                >
                                  <PersonAdd fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            {(org.users || []).length === 0 ? (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No members yet.
                              </Typography>
                            ) : (
                              <Box display="flex" gap={1} flexWrap="wrap">
                                {org.users.map((user) => (
                                  <Chip
                                    key={user.id}
                                    label={
                                      user.first_name && user.last_name
                                        ? `${user.first_name} ${user.last_name}`
                                        : user.email
                                    }
                                    onDelete={() =>
                                      handleRemoveUser(org.id, user.id)
                                    }
                                    deleteIcon={<PersonRemove />}
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
            {!isLoading && organizations.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No organizations yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Organization</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!form.name}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Organization</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEdit}
            disabled={!form.name}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Organization</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{deleteTarget?.name}</strong>? This will also remove all
            associated devices and API keys.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog
        open={!!addUserTarget}
        onClose={() => {
          setAddUserTarget(null);
          setSelectedUser(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add User to {addUserTarget?.name}</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={addUserTarget ? getAvailableUsers(addUserTarget) : []}
            getOptionLabel={(user) =>
              user.first_name && user.last_name
                ? `${user.first_name} ${user.last_name} (${user.email})`
                : user.email
            }
            value={selectedUser}
            onChange={(_, value) => setSelectedUser(value)}
            renderInput={(params) => (
              <TextField {...params} label="Search users" margin="normal" />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddUserTarget(null);
              setSelectedUser(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddUser}
            disabled={!selectedUser}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {snackbar && (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>
    </Container>
  );
};

export default Organizations;
